// Shared layout for every legal / policy page on CareerOS.
// Renders: header · title block · optional TOC · numbered sections · peer nav footer.
//
// Each legal page is a thin data file that imports this component and passes a
// `sections` array. Keep all page bodies as data, never fork the JSX.

import { Link } from "react-router-dom";
import BrandMark from "@/components/BrandMark";

// Ordered legal-page directory — used to render the "Related policies" footer
// on every legal page. Any new legal page must be added here so it links.
export const LEGAL_PAGES = [
  { path: "/terms",          label: "Terms & Conditions" },
  { path: "/privacy",        label: "Privacy Policy" },
  { path: "/cookies",        label: "Cookie Policy" },
  { path: "/ai-policy",      label: "AI Usage & Responsible AI" },
  { path: "/subscription-policy", label: "Subscription & Billing" },
  { path: "/refund-policy",  label: "Refund Policy" },
  { path: "/acceptable-use", label: "Acceptable Use" },
  { path: "/disclaimer",     label: "Disclaimer" },
  { path: "/data-retention", label: "Data Retention" },
  { path: "/accessibility",  label: "Accessibility Statement" },
  { path: "/security",       label: "Security Policy" },
  { path: "/copyright",      label: "Copyright & Notice-and-Notice" },
];

export default function LegalLayout({
  eyebrow = "Legal",
  title,
  subtitle,
  effectiveDate,
  lastUpdated,
  intro,
  sections = [],
  currentPath,
}) {
  const showToc = sections.length >= 4;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border sticky top-0 z-10 bg-background/85 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="legal-home-link">
            <BrandMark size={28} className="rounded-md" />
            <span className="font-display font-bold tracking-tight">Career OS</span>
          </Link>
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            Legal
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16" data-testid={`legal-${currentPath || "page"}`}>
        <div className="mb-8">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
            {eyebrow}
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.05]">
            {title}
          </h1>
          {subtitle && <h2 className="mt-3 text-lg text-muted-foreground">{subtitle}</h2>}
          {(effectiveDate || lastUpdated) && (
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-1 text-xs font-mono text-muted-foreground uppercase tracking-widest">
              {effectiveDate && <span>Effective Date: {effectiveDate}</span>}
              {lastUpdated && <span>Last Updated: {lastUpdated}</span>}
            </div>
          )}
        </div>

        {intro && (
          <div className="prose prose-invert max-w-none prose-p:leading-relaxed prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
            {intro}
          </div>
        )}

        {showToc && (
          <nav
            aria-label="Table of contents"
            className="mt-10 mb-12 card-flat p-6"
            data-testid="legal-toc"
          >
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4">
              Contents
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm list-none pl-0">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="flex gap-3 hover:text-primary transition-colors"
                  >
                    <span className="font-mono text-muted-foreground w-6 shrink-0">
                      {String(s.n).padStart(2, "0")}
                    </span>
                    <span>{s.title}</span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="prose prose-invert max-w-none prose-headings:font-display prose-headings:tracking-tight prose-p:leading-relaxed prose-li:leading-relaxed prose-a:text-primary prose-a:no-underline hover:prose-a:underline space-y-10">
          {sections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <h2 className="font-display font-bold text-2xl">
                <span className="font-mono text-primary mr-3 text-lg align-middle">
                  {String(s.n).padStart(2, "0")}
                </span>
                {s.title}
              </h2>
              <div className="mt-3">{s.body}</div>
            </section>
          ))}
        </div>

        {/* Peer nav — jump between legal pages */}
        <nav
          aria-label="Related policies"
          className="mt-20 pt-8 border-t border-border"
          data-testid="legal-peer-nav"
        >
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4">
            Related policies
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm list-none pl-0">
            {LEGAL_PAGES.filter((p) => p.path !== currentPath).map((p) => (
              <li key={p.path}>
                <Link to={p.path} className="text-muted-foreground hover:text-foreground transition-colors">
                  {p.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-6 text-sm">
            <Link to="/" className="underline">Home</Link>
            <span className="mx-3 text-muted-foreground">·</span>
            <a
              href="#top"
              className="underline"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            >
              Back to top
            </a>
          </div>
        </nav>
      </main>
    </div>
  );
}

/**
 * Convenience helper — turn a "bullet list" content spec into JSX to keep
 * page files terse. Each page can still pass raw JSX if it needs custom
 * markup (blockquotes, tables, embedded links).
 */
export function bullets(items) {
  return (
    <ul>
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}
